/**
 * Layar login konsol — **dua pintu dalam satu layar**, dipilih sadar oleh yang masuk.
 *
 * | Pintu | Kredensial | Boleh menulis master? |
 * |---|---|---|
 * | Kantor pusat | email + kata sandi | ya, sesuai peran |
 * | Di outlet | kode staf + PIN + kode outlet | **tidak** |
 *
 * ⚠️ Tenant diketik di sini, dan hanya di sini. Sesudah token terbit ia hanya dibaca dari token
 * (R-AI-1) — tak pernah dari URL maupun body.
 *
 * ### ⛔⛔ Kotak tenant TIDAK boleh punya bawaan yang dipahat
 * Ia dulu berisi `initialValues={{ tenant: lastTenant() }}`, dan itu salah dua kali:
 *
 * 1. **Pelanggan baru melihat kode pelanggan LAIN sudah terketik.** Hari pertama adalah momen
 *    paling rapuh sebuah onboarding, dan layar yang terisi nama orang lain terbaca sebagai
 *    "saya salah alamat".
 * 2. **Ia ikut ter-bundle.** `tenant:"arena"` terbaca siapa pun yang membuka DevTools — dan itu
 *    melemahkan kontrol yang sengaja dibayar: `console.service.ts` menjawab tenant-tak-ada dan
 *    sandi-salah dengan 401 yang IDENTIK, justru supaya pintu ini tak bisa dipakai memetakan
 *    tenant mana saja yang ada di server. Bundel yang menyerahkan satu nama cuma-cuma
 *    mengurangi nilai kontrol itu tanpa ada yang menyadarinya.
 *
 * ⇒ Yang diingat adalah tenant yang TERAKHIR BERHASIL dipakai di peramban ini, disimpan
 *   `localStorage`. Pengunjung baru melihat kotak kosong (tak ada yang bocor); yang berulang tak
 *   perlu mengetik ulang — dan yang diingat miliknya sendiri, bukan milik orang lain.
 *
 * ⛔ Pesan gagal ditampilkan APA ADANYA dari server, yang memang sengaja sama untuk semua sebab.
 * Layar tak boleh "membantu" menebak ("email tak terdaftar") — tebakan itulah yang mengubah pintu
 * ini jadi alat memetakan akun.
 */
import { Alert, Button, Card, Form, Input, Segmented, Space, Typography } from 'antd';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { api } from '../api/client';
import { saveSession } from '../api/session';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

type Pintu = 'hq' | 'outlet';

/**
 * Tenant terakhir yang BERHASIL dipakai di peramban ini.
 *
 * ⚠️ Ditulis hanya sesudah login berhasil, bukan saat mengetik: menyimpan tiap ketikan berarti
 * tebakan orang lain ikut tersimpan, lalu tersaji ke pengguna sah berikutnya di perangkat bersama.
 */
const TENANT_KEY = 'osbpos.portal.tenant';

function lastTenant(): string {
  try {
    return localStorage.getItem(TENANT_KEY) ?? '';
  } catch {
    return '';
  }
}

export interface LoginProps {
  readonly onLogin: () => void;
}

export function Login({ onLogin }: LoginProps) {
  const { t } = useTranslation();
  const [pintu, setPintu] = useState<Pintu>('hq');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (v: Record<string, string>) => {
    setBusy(true);
    setError(null);
    try {
      const r =
        pintu === 'hq'
          ? await api.loginPassword({
              tenant: v['tenant'] ?? '', email: v['email'] ?? '', password: v['password'] ?? '',
            })
          : await api.loginOutletPin({
              tenant: v['tenant'] ?? '', outletCode: v['outletCode'] ?? '',
              staffCode: v['staffCode'] ?? '', pin: v['pin'] ?? '',
            });
      try {
        localStorage.setItem(TENANT_KEY, r.tenant);
      } catch {
        /* ⚠️ Penyimpanan yang ditolak (mode privat) tak boleh menggagalkan login. */
      }
      saveSession({
        token: r.access_token,
        tenant: r.tenant,
        displayName: r.display_name,
        role: r.role,
        scopes: r.scope.split(' ').filter((s) => s !== ''),
        ...('outlet_code' in r ? { outletCode: (r as { outlet_code: string }).outlet_code } : {}),
        exp: Math.floor(Date.now() / 1000) + r.expires_in,
      });
      onLogin();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login" data-testid="login">
      <Card style={{ width: 'min(430px, 100%)' }}>
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <div className="login-head">
            <Typography.Title level={4} style={{ margin: 0 }}>{t('app.title')}</Typography.Title>
            <LanguageSwitcher />
          </div>

          {/* ⭐ Pilihan pintu di depan, bukan tersembunyi: keduanya sah, dan yang memilih tahu
              sedang berdiri di mana. */}
          <Segmented
            block
            value={pintu}
            onChange={(v) => { setPintu(v as Pintu); setError(null); }}
            options={[
              { value: 'hq', label: <span data-testid="pintu-hq">{t('login.doorHq')}</span> },
              { value: 'outlet', label: <span data-testid="pintu-outlet">{t('login.doorOutlet')}</span> },
            ]}
          />

          <Form layout="vertical" initialValues={{ tenant: lastTenant() }} onFinish={(v) => void submit(v)}>
            <Form.Item name="tenant" label={t('login.tenant')} rules={[{ required: true }]}>
              <Input data-testid="tenant" />
            </Form.Item>

            {pintu === 'hq' ? (
              <>
                <Form.Item name="email" label={t('login.email')} rules={[{ required: true }]}>
                  <Input type="email" autoComplete="username" data-testid="email" />
                </Form.Item>
                <Form.Item name="password" label={t('login.password')} rules={[{ required: true }]}>
                  <Input.Password autoComplete="current-password" data-testid="password" />
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item name="outletCode" label={t('login.outletCode')} rules={[{ required: true }]}>
                  <Input data-testid="outlet-code" />
                </Form.Item>
                <Form.Item name="staffCode" label={t('login.staffCode')} rules={[{ required: true }]}>
                  <Input data-testid="staff-code" />
                </Form.Item>
                <Form.Item name="pin" label={t('login.pin')} rules={[{ required: true }]}>
                  <Input.Password inputMode="numeric" data-testid="pin" />
                </Form.Item>
                {/* ⭐ Batasnya disebut DI DEPAN, bukan ditemukan saat tombolnya hilang. */}
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  data-testid="pin-catatan"
                  title={<Trans i18nKey="login.pinNote" components={{ 1: <b /> }} />}
                />
              </>
            )}

            {error !== null && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                data-testid="login-error"
                title={error}
              />
            )}

            <Button type="primary" htmlType="submit" block loading={busy} data-testid="masuk">
              {busy ? t('login.checking') : t('login.submit')}
            </Button>
          </Form>
        </Space>
      </Card>
    </main>
  );
}
